import { OrderSource } from '../ticket/ticket.types';

export enum KitchenHubEventEnum {
  RECEIVED = 'received',
  CANCELLED = 'cancelled',
}

export enum KitchenHubIntegrationPartners {
  Doordash = 'Door Dash',
  UberEats = 'UberEats',
  GrubHub = 'Grub hub',
  DoordashDrive = 'Door Dash Drive',
}

// Enum or union type for price indicators
export enum PriceIndicator {
  Increase = 'Increase',
  Decrease = 'Decrease',
}

export type DeliveryPlatform = {
  active: boolean;
  provider: KitchenHubProviders;
  percentage: number;
  priceIndicator: PriceIndicator;
};

export enum KitchenHubProviders {
  DOORDASH = 'doordash_pos',
  GRUBHUB = 'grubhub_pos',
  UBER_EATS = 'ubereats',
  DoordashDrive = 'doordash_drive',
}

export type KitchenHubProvidersWithPricing = Exclude<KitchenHubProviders, KitchenHubProviders.DoordashDrive>;

export interface KitchenHubEventPayload {
  restaurantId: number;
  businessId: number;
  activeProviders: KitchenHubProviders[];
}

export interface KitchenHubLocationType {
  id: string;
  partner_location_id: string;
  location_name: string;
  location_street: string;
  location_city: string;
  location_state: string;
  location_zipcode: string;
  location_timezone: string;
  location_country: string;
  created_at: string;
  restaurant_delivery_time: number;
  pickup_notes: string | null;
}
export interface KitchenHubLocationOnboardingType {
  location_name: string;
  location_street: string;
  location_city: string;
  location_state: string;
  location_zipcode: string;
  partner_location_id: number;
  location_timezone?: string;
  location_country?: string;
  pickup_notes?: string;
  created_at?: string;
  restaurant_delivery_time?: number;
}

export interface KitchenHubStoreType {
  id: string;
  partner_store_id: string;
  store_name: string;
  location_id: string;
  created_at: string;
  auto_accept_enabled: boolean;
  auto_complete_enabled: boolean;
  auto_accept_cooking_time: number;
  restaurant_delivery_time: number;
}

export interface KitchenHubStoreOnboardingType {
  location_id: string;
  store_name: string;
  partner_store_id: number;
}

export interface KitchenHubProviderOnboardingType {
  integrationPartner: KitchenHubProviders;
  redirectUrl: string;
}

export enum OrderEventStatus {
  created = 'OrderCreated',
  updated = 'OrderUpdated',
  // Add more status
}

export enum KitchenHubEnums {
  PICKUP = 'pickup',
  // we will verify the rest of enum from kitchenHhub
  UBEREATS = 'ubereats',
  DOORDASH = 'doordash',
  PAYMENT_STATUS_PAID = 'PAID',
  ORDER_ACCEPTED = 'ACCEPTED',
  ORDER_REJECTED = 'REJECTED',
}

export enum KitchenHubOrderStatus {
  NEW = 'new',
  ACCEPTED = 'accept',
  COMPLETED = 'complete',
  CANCELED = 'cancel',
}

export const ProviderToOrderSourceMapper: Record<string, OrderSource> = {
  ubereats: OrderSource.UberEats,
  doordash_pos: OrderSource.Doordash,
  grubhub: OrderSource.GrubHub,
  doordash: OrderSource.Doordash,

  // TODO: Once we extend the OrderSource enum and update the database schema,
  // replace these temporary `POS` mappings with their actual provider-specific values.
  foodhub: OrderSource.POS,
  chownow: OrderSource.POS,
  eatstreet: OrderSource.POS,
  cloudwaitress: OrderSource.POS,
  delivery: OrderSource.POS,
  skipthedish: OrderSource.POS,
  grubhub_pos: OrderSource.POS,
  beyondmenu: OrderSource.POS,
  gloriafood: OrderSource.POS,
  flipdish: OrderSource.POS,
  delivery_logic_marketplace: OrderSource.POS,
  menufy: OrderSource.POS,
};

export interface resultSchema {
  kitchenHubStoreId: string;
  partnerName: string;
  integrationAccountId: string;
  connection: string;
}
export interface kitchenHubIntegrationsScheme {
  integration_account_id: string;
  store_id: string;
  connection: string;
}

export enum KitchenHubIntegrationStatus {
  CONNECTED = 'connected',
  IN_PROGRESS = 'in_progress',
  DISABLED = 'disabled',
  REJECTED = 'rejected',
  WAITING = 'waiting',
  WAITING_MENU = 'waiting_menu',
  UNKNOWN = 'unknown',
}

export enum KitchenHubIntegrationOnlineStatus {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export const KitchenHubProviderStatusMapperUI: Record<KitchenHubIntegrationStatus, string> = {
  [KitchenHubIntegrationStatus.CONNECTED]: 'Connected',
  [KitchenHubIntegrationStatus.IN_PROGRESS]: 'In Progress',
  [KitchenHubIntegrationStatus.DISABLED]: 'Disabled',
  [KitchenHubIntegrationStatus.REJECTED]: 'Rejected',
  [KitchenHubIntegrationStatus.WAITING]: 'Waiting',
  [KitchenHubIntegrationStatus.WAITING_MENU]: 'Waiting for Menu',
  [KitchenHubIntegrationStatus.UNKNOWN]: 'Connect',
};

export enum orderStatus {
  ACCEPTED = 'accepted',
  COMPLETED = 'completed',
  CANCELED = 'cancelled',
}

export type KitchenHubOrder = {
  id: number;
  external_id: string;
  number: string;
  daily_number: number;
  type: string;
  status: orderStatus;
  notes: string;
  paid: boolean;
  asap: boolean;
  add_disposable_items: boolean;
  prep_time_minutes: number | null;
  table_number: string | null;
  cancelled_by: string | null;
};

export type KitchenHubProvider = {
  id: string;
  name: KitchenHubProviders;
  merchant_id: string;
  store_id: string;
};

export type KitchenHubStore = {
  id: string;
  name: string;
  partner_store_id: string;
};

export type KitchenHubCustomer = {
  name: string;
  email: string | null;
  phone_number: string;
  phone_code: string | null;
};

export type KitchenHubOption = {
  id: string | null;
  partner_id: string;
  name: string;
  quantity: number;
  price: string;
  base_price: string | null;
  modifier_name: string;
  options: any[]; // You can replace 'any' with a proper nested type if known
};

export type KitchenHubItem = {
  id: string;
  partner_id: string;
  name: string;
  quantity: number;
  price: string;
  base_price: string;
  instructions: string | null;
  is_alcohol: boolean;
  options: KitchenHubOption[];
};

export type KitchenHubDelivery = {
  type: string;
  status: string | null;
  notes: string;
  address: {
    country: string;
    street: string;
    city: string;
    state: string;
    zipcode: string;
    unit_number: string | null;
    latitude: number | null;
    longitude: number | null;
  };
  full_address: string;
  courier: {
    name: string;
    photo_url: string | null;
    phone_number: string;
    status: string | null;
  };
  service: string | null;
  cancellation_reason: string | null;
};

export type KitchenHubLocation = {
  id: string;
  partner_location_id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  lat: number | null;
  lon: number | null;
};

export type KitchenHubPayment = {
  method: string;
};

export type KitchenHubCharges = {
  subtotal: string;
  tax: string;
  tax_payout: number;
  restaurant_tip_amount: string | null;
  adjustment: string | null;
  tips: string;
  discount: string;
  total: string;
  delivery_fee: string | null;
  service_fee: string | null;
  processing_fee: string | null;
  commission: string | null;
  payout: string | null;
  other_fee: Record<string, any>;
};

export type KitchenHubTimestamps = {
  placed_at: string;
  created_at: string;
  pickup_at: string;
  accepted_at: string | null;
  cancelled_at: string | null;
  completed_at: string | null;
  delivery_at: string;
  scheduled_for: string;
};

export type KitchenHubPayload = {
  event_data: {
    event_type: string;
    status: OrderEventStatus;
  };
  order: KitchenHubOrderDetails;
};

export type KitchenHubOrderDetails = {
  order: KitchenHubOrder;
  provider: KitchenHubProvider;
  store: KitchenHubStore;
  customer: KitchenHubCustomer;
  items: KitchenHubItem[];
  delivery: KitchenHubDelivery;
  payment: KitchenHubPayment;
  charges: KitchenHubCharges;
  timestamps: KitchenHubTimestamps;
  location: KitchenHubLocation;
};

export type WorkingHour = {
  days: string[];
  time_slots: { start_time: string; end_time: string }[];
};

export type workingSlot = {
  days: string[];
  time_slots: {
    start_time: string;
    end_time: string;
  }[];
};

// KitchenHub + Door Dash

export interface DeliveryWebhookDto {
  event_data: {
    webhook_type: string;
    event_name: string;
    version: number;
  };
  restaurant_data: {
    restaurant_id: string;
    proxy_restaurant_id: string;
    partner_store_id: string;
  };
  delivery_data: {
    id: number;
    order_id: string;
    order_number: string | null;
    order_external_id: string | null;
    scheduled_order: boolean;
    delivery_order_id: string;
    cancellation_reason: string | null;
    pickup_info: {
      restaurant_name: string;
      restaurant_address: string;
      pickup_datetime: string;
      pickup_notes: string | null;
      store_name: string;
      store_address: string;
      estimated_pickup_at: string | null;
    };
    customer_info: {
      first_name: string;
      last_name: string;
      phone_number: string;
      email: string | null;
    };
    drop_off_info: {
      drop_off_address: string;
      drop_off_datetime: string | null;
      drop_off_notes: string | null;
      estimated_dropoff_at: string | null;
    };
    charges: {
      total: number;
      delivery_fee: number;
      delivery_tips: number;
      subtotal: number | null;
    };
    status: string;
    provider_id: string | null;
    courier_info: {
      id: number;
      name: string;
      phone_number: string;
      phone_code: string | null;
      photo_url: string | null;
      vehicle_type: string;
    };
    delivery_type: string;
    tracking_url: string | null;
    created_at: string;
    delivered_at: string | null;
    canceled_at: string | null;
    accepted_at: string | null;
    is_mock_delivery: boolean;
    items: {
      name: string;
      quantity: number;
      price: string;
      is_alcohol: boolean;
    }[];
    store_id: string;
    virtual_brand_company_id: string;
    delivery_provider_id: string;
    delivery_at: string | null;
  };
}
